import React, { useState, useContext, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Typography,
  Layout,
  Row,
  Col,
  Alert,
  Modal,
  Divider,
} from "antd";
import { UserOutlined, LockOutlined, MailOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppContext from "../../AppContext";
import axios from "axios";
import { BASE_URL, RoleEnum } from "../../Constants";
import LanguageSwitcher from "../LanguageSwitcher";
import { getAndClearRedirectUrl } from "../../utils/redirectUtils";

const { Title } = Typography;
const { Content, Footer } = Layout;

const LOGIN_CARD_CSS = `
  .fc-login-card { position: relative; background: rgba(255,255,255,0.96); padding: 36px 32px 28px; border-radius: 20px;
    box-shadow: 0 24px 60px rgba(15,10,60,0.28), 0 2px 6px rgba(15,10,60,0.08); border: 1px solid rgba(255,255,255,0.7); }
  .fc-login-title.ant-typography { text-align: center; margin: 0 0 24px; font-weight: 700; color: #1e1b4b; letter-spacing: -0.2px; }
  .fc-login-card .ant-input-affix-wrapper-lg { border-radius: 10px; padding: 10px 14px; }
  .fc-login-card .ant-input-affix-wrapper:focus, .fc-login-card .ant-input-affix-wrapper-focused { border-color: #312e81; box-shadow: 0 0 0 3px rgba(49,46,129,0.12); }
  .fc-login-card .ant-input-prefix { color: #6b7280; margin-inline-end: 10px; }
  .fc-login-card input:-webkit-autofill { -webkit-box-shadow: 0 0 0 40px #fff inset; transition: background-color 9999s; }
  .fc-login-btn.ant-btn { width: 100%; height: 46px; border-radius: 10px; font-weight: 600; background: #1e1b4b; border-color: #1e1b4b; color: #fff; }
  .fc-login-btn.ant-btn:hover, .fc-login-btn.ant-btn:focus { background: #312e81 !important; border-color: #312e81 !important; color: #fff !important; }
  .fc-login-links { display: flex; justify-content: center; gap: 20px; margin-top: 4px; }
  .fc-login-links .ant-btn-link { padding: 0; height: auto; font-size: 14px; color: #4338ca; }
  .fc-login-links .ant-btn-link:hover { color: #1e1b4b; }
  .fc-login-lang { display: flex; justify-content: flex-end; margin: -12px -8px 8px; }
  .fc-login-lang button { background: transparent; color: #4338ca; box-shadow: none; padding: 4px 10px; font-size: 13px; font-weight: 600; border: 1px solid #e0e7ff; border-radius: 999px; }
  .fc-login-lang button:hover { background: #eef2ff; }
  @media (max-width: 480px) { .fc-login-card { padding: 28px 20px 22px; } .fc-login-links { flex-direction: column; align-items: center; } }
`;

const Login = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [showForgotUsername, setShowForgotUsername] = useState(false);
  const [forgotUsernameLoading, setForgotUsernameLoading] = useState(false);
  const authCtx = useContext(AppContext);
  const [searchParams] = useSearchParams();

  // The "forgot username" email links here with ?username=… so only the password is left to type.
  const prefillUsername = searchParams.get("username");
  useEffect(() => {
    if (prefillUsername) form.setFieldsValue({ username: prefillUsername });
  }, [prefillUsername, form]);

  // Digital Card company-admin paths a virtual (Individual Event) account must never land on,
  // even via a stale `redirect_after_login` value or a `?redirect=` link from an old session.
  const VIRTUAL_BLOCKED_REDIRECT_PREFIXES = [
    "/companies",
    "/company-form",
    "/employees",
    "/employee-form",
    "/settings",
  ];

  // Handle redirect after login
  const handlePostLoginRedirect = (userData) => {
    // First check for stored redirect URL
    const storedRedirectUrl = getAndClearRedirectUrl();

    // Then check URL parameter
    const redirectParam = searchParams.get('redirect');

    let redirectUrl = storedRedirectUrl || redirectParam;

    if (redirectUrl && userData.isVirtual) {
      const decodedPath = decodeURIComponent(redirectUrl).split('?')[0];
      if (VIRTUAL_BLOCKED_REDIRECT_PREFIXES.some((prefix) => decodedPath.startsWith(prefix))) {
        redirectUrl = null;
      }
    }

    if (redirectUrl && redirectUrl !== '/') {
      navigate(decodeURIComponent(redirectUrl), { replace: true });
      return;
    }

    // Individual Events/Wedding accounts (virtual company) land directly on Events, their
    // primary surface. Only an account with a payment still pending is sent to My Packages
    // (to pay & activate). If /events is not allowed for any other reason, the route guard
    // falls back to "/", where DashboardRouter sends the account to My Packages.
    if (userData.isVirtual) {
      const isPendingPayment = userData.subscriptionState === "pending_payment";
      navigate(isPendingPayment ? "/my-packages" : "/events", { replace: true });
      return;
    }

    // Default navigation based on role
    if (userData.role === RoleEnum.SUPER_ADMIN) {
      navigate("/companies");
    } else if (userData.role === RoleEnum.PUBLIC_EMPLOYEE) {
      navigate("/");
    } else {
      navigate("/");
    }
  };

  const showSubscriptionExpiredModal = () => {
    Modal.error({
      title: t("messages.error.subscriptionExpired"),
      content: t("messages.error.subscriptionExpiredDetails"),
      okText: t("buttons.contactSupport"),
      onOk: () => {
        navigate("/");
      },
    });
  };

  const onFinish = async (values) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${BASE_URL}/Auth/login`, values);
      setLoading(false);

      // A virtual (Wedding/Event) account that was provisioned at signup but never
      // completed payment is also "not subscribed" but should recover through the
      // existing My Packages → Pay & Activate Now flow, not the expired-subscription
      // block. handlePostLoginRedirect already sends a virtual account without event
      // access to /my-packages, so logging in normally here is enough to reach it.
      const isPendingPayment = res.data.subscriptionState === "pending_payment";

      if (
        res.data.isSubscribed === false &&
        !isPendingPayment &&
        ![RoleEnum.SUPER_ADMIN, RoleEnum.PUBLIC_EMPLOYEE].includes(res.data.role)
      ) {
        showSubscriptionExpiredModal();
        return;
      }

      authCtx.login(res.data);

      // Handle redirect after successful login
      handlePostLoginRedirect(res.data);
    } catch (err) {
      setLoading(false);
      console.log(err);
      if (err.response?.data?.isSubscriptionExpired && err.response?.data?.role !== RoleEnum.PUBLIC_EMPLOYEE) {
        showSubscriptionExpiredModal();
      } else if (
        err.response?.status === 409 &&
        err.response?.data?.code === "ACCOUNT_PROVISIONING_INCOMPLETE"
      ) {
        setError(
          err.response.data.message ||
            "Your account is still being set up. Please try again in a minute."
        );
      } else {
        setError(t("messages.error.invalidCredentials"));
      }
    }
  };

  const onForgotPasswordFinish = async (values) => {
    setForgotPasswordLoading(true);
    try {
      await axios.post(
        `${BASE_URL}/Auth/ResetPassword?username=${values.username}`
      );
      setForgotPasswordLoading(false);
      setShowForgotPassword(false);
      Modal.success({
        title: t("messages.success.passwordReset"),
        content: t("messages.success.passwordResetDetails"),
      });
    } catch (err) {
      setForgotPasswordLoading(false);
      console.log(err);
      setError(t("messages.error.passwordResetFailed"));
    }
  };

  const onForgotUsernameFinish = async (values) => {
    setForgotUsernameLoading(true);
    try {
      await axios.post(`${BASE_URL}/Auth/ForgotUsername`, { email: values.email });
      setForgotUsernameLoading(false);
      setShowForgotUsername(false);
      Modal.success({
        title: t("messages.success.usernameSent"),
        content: t("messages.success.usernameSentDetails"),
      });
    } catch (err) {
      setForgotUsernameLoading(false);
      setError(t("messages.error.usernameRecoveryFailed"));
    }
  };

  return (
    <Layout
      className="overflow-hidden relative"
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Blurred background */}
      <div
        className="absolute inset-0 bg-cover bg-center z-0"
        style={{
          backgroundImage:
            "url('https://img.freepik.com/free-photo/business-partners-handshake-global-corporate-with-technology-concept_53876-102615.jpg?t=st=1727691319~exp=1727694919~hmac=f4ce481d9edd1546e48a8c8220611b0ad64d4297861155ffc46fc201894a527b&w=996')",
          filter: "blur(8px)",
          transform: "scale(1.1)",
        }}
      ></div>

      {/* Main content */}
      <Content
        className="relative z-10"
        style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Row justify="center" align="middle" style={{ width: "100%" }}>
          <Col xs={22} sm={16} md={12} lg={8} xl={6}>
            <div className="fc-login-card">
              <style>{LOGIN_CARD_CSS}</style>
              <div className="fc-login-lang">
                <LanguageSwitcher />
              </div>
              <Title level={3} className="fc-login-title">
                {showForgotUsername
                  ? t("auth.forgotUsername")
                  : showForgotPassword
                  ? t("auth.resetPassword")
                  : t("auth.signIn")}
              </Title>
              {error && (
                <Alert message={error} type="error" showIcon className="mb-4" />
              )}
              {showForgotUsername ? (
                <Form
                  name="forgotUsername"
                  onFinish={onForgotUsernameFinish}
                  layout="vertical"
                >
                  <p className="text-center text-gray-600 mb-4">
                    {t("auth.forgotUsernameHint")}
                  </p>
                  <Form.Item
                    name="email"
                    rules={[
                      { required: true, message: t("auth.emailRequired") },
                      { type: "email", message: t("auth.emailInvalid") },
                    ]}
                  >
                    <Input
                      size="large"
                      type="email"
                      prefix={<MailOutlined className="site-form-item-icon" />}
                      placeholder={t("placeholders.enterEmail")}
                    />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      size="large"
                      className="fc-login-btn"
                      loading={forgotUsernameLoading}
                    >
                      {t("buttons.sendUsername")}
                    </Button>
                  </Form.Item>
                  <Divider style={{ margin: "8px 0 12px" }} />
                  <div className="text-center">
                    <Button
                      type="link"
                      onClick={() => { setShowForgotUsername(false); setError(""); }}
                    >
                      {t("buttons.backToLogin")}
                    </Button>
                  </div>
                </Form>
              ) : showForgotPassword ? (
                <Form
                  form={form}
                  name="forgotPassword"
                  onFinish={onForgotPasswordFinish}
                  layout="vertical"
                >
                  <Form.Item
                    name="username"
                    rules={[
                      {
                        required: true,
                        message: t("form.validation.usernameRequired"),
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      prefix={<UserOutlined className="site-form-item-icon" />}
                      placeholder={t("placeholders.enterUsername")}
                    />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      size="large"
                      className="fc-login-btn"
                      loading={forgotPasswordLoading}
                    >
                      {t("buttons.resetPassword")}
                    </Button>
                  </Form.Item>
                  <Divider style={{ margin: "8px 0 12px" }} />
                  <div className="text-center">
                    <Button
                      type="link"
                      onClick={() => setShowForgotPassword(false)}
                    >
                      {t("buttons.backToLogin")}
                    </Button>
                  </div>
                </Form>
              ) : (
                <Form
                  form={form}
                  name="login"
                  onFinish={onFinish}
                  layout="vertical"
                >
                  <Form.Item
                    name="username"
                    rules={[
                      {
                        required: true,
                        message: t("form.validation.usernameRequired"),
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      prefix={<UserOutlined className="site-form-item-icon" />}
                      placeholder={t("placeholders.enterUsername")}
                    />
                  </Form.Item>
                  <Form.Item
                    name="password"
                    rules={[
                      {
                        required: true,
                        message: t("form.validation.passwordRequired"),
                      },
                    ]}
                  >
                    <Input.Password
                      size="large"
                      prefix={<LockOutlined className="site-form-item-icon" />}
                      placeholder={t("placeholders.enterPassword")}
                    />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      size="large"
                      className="fc-login-btn"
                      loading={loading}
                    >
                      {loading ? t("buttons.loggingIn") : t("buttons.login")}
                    </Button>
                  </Form.Item>
                  <div className="fc-login-links">
                    <Button
                      type="link"
                      onClick={() => setShowForgotPassword(true)}
                    >
                      {t("auth.forgotPassword")}
                    </Button>
                    <Button
                      type="link"
                      onClick={() => { setShowForgotUsername(true); setError(""); }}
                    >
                      {t("auth.forgotUsername")}
                    </Button>
                  </div>
                </Form>
              )}
            </div>
          </Col>
        </Row>
      </Content>

      {/* Always visible footer */}
      <Footer
        className="relative z-10 text-center text-white"
        style={{
          background: "transparent",
          color: "#fff",
        }}
      >
        © 2026{" "}
        <a
          href="https://www.future-horses.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#fff", textDecoration: "underline" }}
        >
          Future Horses
        </a>
        . All rights reserved.
      </Footer>

      <div
  className="absolute"
  style={{ top: 20, left: 20, zIndex: 50 }}
>
  <a
    href="https://www.future-horses.com"
    target="_blank"
    rel="noopener noreferrer"
  >
    <img
      src="/future-cards-logo(4).png"
      alt="Future Horses Logo"
      style={{ height: "50px", objectFit: "contain" }}
    />
  </a>
</div>

      {/* Made Arabia Logo - Top Right */}
      <div
        className="absolute"
        style={{ top: 20, right: 20, zIndex: 50 }}
      >
        <img
          src="/madeEng.-Horizontal-cmyk.png"
          alt="Made Arabia Logo"
          style={{ height: "50px", objectFit: "contain", mixBlendMode: "multiply" }}
        />
      </div>
    </Layout>
  );
};

export default Login;
